import { SalesChannelType, TODO } from '../core';
import { CartEventOrderForm, EventDetail } from '../event';
import { AutorenewalType, CartMembershipOrderForm } from '../membership';

/**
 * Unified cart order form type that can be either an event or membership form
 */
export type CartOrderForm = CartEventOrderForm | CartMembershipOrderForm;

export type CartFormItem = {
  uuid: string
  itemUUID: string | null
  formId: string
  itemId: string
  itemName: string
  itemType: string
  oncePerCart: boolean
  formCategoryType: string
  formDisplaySettings: string
  formResponseId: string | null
}

/**
 * Represents an event item payload used for cart operations (add/update/remove)
 */
export interface CartEventItem {
  itemId?: string;
  quantity: number;
  isRetail: boolean;
  itemType: 'Inventory' | 'Event';
  eventId: string;
  displayOrder: number;
  ticketingTypeId: string | null;
  inventoryId: string | null;
  ticketingTypeName: string | null;
  inventoryName: string | null;
  unitPrice: string | null;
  retailUnitPrice: string | null;
  ignoreEntitlements: boolean;
}

/**
 * Represents a ticket item in a cart event
 */
export interface CartEventTicket {
  itemId: string;
  eventId: string;
  ticketingTypeId: string;
  eventName: string;
  ticketingTypeName: string;
  quantity: number;
  unitPrice: string;
  retailUnitPrice: string;
  amount: string;
  itemType: 'Event';
  admissionType: string;
  isRetail: boolean;
  ignoreEntitlements: boolean;
  displayOrder: number;
  resolvedQuantity: number;
  ticketingTypeDescription: string;
  csrDisabled: number;
  invalid: boolean;
  total: number;
  maxLimit: number;
  groupQuantity: number;
}

/**
 * Represents an add-on item in a cart event
 */
export interface CartEventAddOn {
  itemId: string;
  eventId: string;
  eventName: string;
  inventoryId: number;
  inventoryName: string;
  quantity: number;
  unitPrice: string;
  retailUnitPrice: string;
  amount: string;
  itemType: 'Inventory';
  admissionType: string;
  isRetail: boolean;
  ignoreEntitlements: boolean;
  displayOrder: number;
  resolvedQuantity: number;
  description: string;
  invalid: boolean;
  total: number;
  /** Whether CSR-only channel is disabled */
  csrDisabled?: boolean;
}

/**
 * Represents an event in the cart with its tickets and add-ons
 */
export interface CartEvent {
  eventId: string;
  itemType?: 'event';
  tickets: CartEventTicket[];
  addOns: CartEventAddOn[];
  ignoreLimits?: boolean;
  /** Full event details, populated after fetching from API */
  event?: EventDetail;
  /** Reservation error data if reservation failed */
  reservationError?: TODO;
  /** Whether ticket limits have been checked for this event */
  limitsChecked?: boolean;
  /** Whether the event has a ticket error */
  hasTicketError?: boolean;
  /** Whether the event has a restricted event error */
  restrictedEventError?: boolean;
}

export interface ShoppingCart {
  /** If it's an existing shopping cart then the id of the cart. */
  id?: string;
  /**
   * ACME internal use only. Use `customerId` below.
   * @internal
   * */
  tempVisitorId?: string;
  /**
   * ACME internal use only. Use `customerId` below.
   * @internal
   * */
  visitorId?: string;
  /**
   * ACME internal use only. Use `membershipIds` below.
   * @internal
   * */
  membershipId?: string;
  /**
   * The ID of the level.
   * If you don't have the membership id but know the membership level then you can pass this in to give them membership discounts and access.
   * If you pass in a membership id then this will not be used.
   */
  membershipCategoryId?: string;
  /**
   * ACME internal use only.
   * Use couponCodes below.
   * Populating this field will cause the coupons to not be applied to the cart.
   * @internal
   */
  couponCode?: string;
  /**
   * A list of items in the shopping cart.
   */
  items: ShoppingCartItem[];
  /**
   * A list of combo items in the cart.
   * This is for a combo event not combo ticket category.
   */
  comboItems?: ShoppingCartComboItem[];
  /**
   * ACME internal use only.
   * @internal
   */
  forms?: any[];
  /**
   * A list of coupon codes being applied to this cart.
   */
  couponCodes?: string[];
  /**
   * The id of an existing customer.
   * This is ignored when creating a cart outside of checkout. A new customer can be created during B2C Checkout.
   */
  customerId?: string;
  /**
   * A list of membership IDs that are being used on the order
   */
  membershipIds?: string[];
  /**
   * ACME internal use only.
   * @internal
   */
  orderContact?: string;
  /**
   * The sales channel that this order was completed on.
   * one of (online, customerRep, pointOfSale, reseller, kiosk or collaborator)
   */
  saleChannel: SalesChannelType;
  /**
   * ACME internal use only.
   * @internal
   */
  org?: string;
}

export type ShoppingCartItem = object;

export type ShoppingCartComboItem = object;

export interface AutorenewInCart {
  autorenewalType: AutorenewalType;
  /**
   * `autorenewalType` is set to `'optional'` or `'required'`
   */
  isAutorenew: boolean;
  /**
   * Member has opted in to Autorenew
   */
  enabled: boolean;
}

export function createAutorenewInCart(
  values: Partial<Pick<AutorenewInCart, 'autorenewalType' | 'enabled'>> = {},
): AutorenewInCart {
  return {
    autorenewalType: values.autorenewalType || 'none',
    isAutorenew: ['optional', 'required'].includes(values.autorenewalType ?? ''),
    enabled: values.enabled || false,
  };
}
